import type { LeaderboardItem, LeaderboardScope } from "./types";

const PODIUM_SIZE = 3;

/**
 * Returns the three podium finishers ordered for display — second, first,
 * third — so the tallest block sits in the middle. Returns an empty array
 * unless all three ranks are present.
 */
export const getPodiumItems = (items: LeaderboardItem[]): LeaderboardItem[] => {
	const first = items.find((item) => item.rank === 1);
	const second = items.find((item) => item.rank === 2);
	const third = items.find((item) => item.rank === 3);

	if (!first || !second || !third) {
		return [];
	}

	return [second, first, third];
};

/**
 * The rows rendered in the rankings list. Starts below the podium when the
 * podium is shown, and stops at the configured cutoff.
 */
export const getRankedItems = (
	items: LeaderboardItem[],
	{
		showUsersPodium,
		numUsersToDisplay,
	}: { showUsersPodium: boolean; numUsersToDisplay: number },
): LeaderboardItem[] => {
	const startRank = showUsersPodium ? PODIUM_SIZE + 1 : 1;

	return items.filter(
		(item) => item.rank >= startRank && item.rank <= numUsersToDisplay,
	);
};

/**
 * The pinned current-user bar only appears when the user is ranked but falls
 * outside the visible list — otherwise their row is already on screen.
 */
export const shouldShowCurrentUserBar = ({
	currentUserItem,
	scope,
	numUsersToDisplay,
}: {
	currentUserItem: LeaderboardItem | undefined;
	scope: LeaderboardScope;
	numUsersToDisplay: number;
}): boolean => {
	if (!currentUserItem || scope !== "global") {
		return false;
	}

	return currentUserItem.rank > numUsersToDisplay;
};

/** Mirrors getRankedItems so the skeleton matches the loaded row count. */
export const getSkeletonRowCount = ({
	numUsersToDisplay,
	showUsersPodium,
}: {
	numUsersToDisplay: number;
	showUsersPodium: boolean;
}): number =>
	Math.max(0, numUsersToDisplay - (showUsersPodium ? PODIUM_SIZE : 0));
